// IndexedDB cache for large datasets
const DB_NAME = 'SevaScribeCache';
const STORE_NAME = 'donations';
const DB_VERSION = 1;

let db = null;

// Initialize IndexedDB
const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = database.createObjectStore(STORE_NAME, { keyPath: 'key' });
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

// Save data to IndexedDB
export const saveToCacheDB = async (key, data) => {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const cacheData = {
      key,
      data,
      timestamp: Date.now()
    };
    
    await store.put(cacheData);
    return true;
  } catch (error) {
    return false;
  }
};

// Get data from IndexedDB
export const getFromCacheDB = async (key, maxAge = 5 * 60 * 1000) => {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      
      request.onsuccess = () => {
        const result = request.result;
        
        if (!result) {
          resolve(null);
          return;
        }
        
        // Check if cache is still valid
        const age = Date.now() - result.timestamp;
        if (age > maxAge) {
          resolve(null);
          return;
        }
        
        resolve(result.data);
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error reading from IndexedDB:', error);
    return null;
  }
};

// Clear cache
export const clearCacheDB = async (key) => {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    if (key) {
      await store.delete(key);
    } else {
      await store.clear();
    }
    return true;
  } catch (error) {
    console.error('Error clearing cache:', error);
    return false;
  }
};

// Get cache info
export const getCacheInfo = async () => {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      
      request.onsuccess = () => {
        const results = request.result;
        const info = results.map(item => ({
          key: item.key,
          timestamp: new Date(item.timestamp).toLocaleString(),
          age: Math.round((Date.now() - item.timestamp) / 1000) + 's',
          size: JSON.stringify(item.data).length
        }));
        resolve(info);
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting cache info:', error);
    return [];
  }
};
